import { useEffect, useState } from 'react';
import { Box, Group, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import type { ReactElement } from 'react';
import type { CohortKind } from './InsightsTypes';
import InitialAvatar from '@/design/InitialAvatar';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import InsightsSection from './InsightsSection';
import InsightsActivitySection from './InsightsActivitySection';
import InsightsEfficiencySection from './InsightsEfficiencySection';
import InsightsCostSection, { computeCostModel, usd } from './InsightsCostSection';
import InsightsFrictionSection from './InsightsFrictionSection';
import InsightsPlanSection from './InsightsPlanSection';
import InsightsRangeControl from './InsightsRangeControl';
import InsightsScorecardSection from './InsightsScorecardSection';
import InsightsAdoptionSection from './InsightsAdoptionSection';
import InsightsTeamSection from './InsightsTeamSection';
import InsightsUseCasesSection from './InsightsUseCasesSection';
import { InsightsViewModel } from './InsightsViewModel';
import InsightsWinsSection from './InsightsWinsSection';

function periodDays(startIso: string, endIso: string): number {
    return Math.max(1, Math.round((Date.parse(endIso) - Date.parse(startIso)) / 86_400_000));
}

const COHORT_LABELS: Record<CohortKind, string> = { team: 'Team', department: 'Department', company: 'Company' };

/** The AI-fluency scorecard — the app's landing page, backed by real per-user telemetry (user view) and directory-based team rollups (team view). */
export default function InsightsPage(): ReactElement {
    const vm = useViewModel(InsightsViewModel);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    const snapshot = vm.snapshot;

    const [cohortKind, setCohortKind] = useState<CohortKind>('team');
    const standing = snapshot?.standing ?? null;
    const cohortOptions = [
        { kind: 'team' as const, cohort: standing?.team },
        { kind: 'department' as const, cohort: standing?.department },
        { kind: 'company' as const, cohort: standing?.company },
    ]
        .filter((option) => option.cohort != null)
        .map((option) => ({ value: option.kind, label: COHORT_LABELS[option.kind] }));
    const effectiveCohortKind: CohortKind =
        standing && standing[cohortKind] ? cohortKind : (cohortOptions[0]?.value ?? 'team');
    const selectedCohort = standing ? standing[effectiveCohortKind] : null;

    const header = (
        <Box
            style={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                background: 'var(--bm-color-canvas)',
            }}
        >
        <PageHeaderComponent
            eyebrow="Insights"
            title={snapshot?.userDisplayName ?? 'Your scorecard'}
            subtitle={
                snapshot
                    ? `${[snapshot.userTitle, snapshot.department].filter((v) => v.length > 0).join(' · ')}${snapshot.userTitle || snapshot.department ? ' — ' : ''}${snapshot.period.label}`
                    : 'Loading…'
            }
            leading={<InitialAvatar source={snapshot?.userDisplayName ?? 'You'} size={40} />}
            rightToolbar={
                <Group gap="sm" wrap="wrap" align="center">
                    <Group gap={6} wrap="nowrap" align="center">
                        <Tooltip label="Switch between your personal scorecard and your team's aggregate view" withArrow multiline w={220} position="bottom">
                            <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: 10, letterSpacing: '0.04em', cursor: 'help' }}>
                                View
                            </Text>
                        </Tooltip>
                        <SegmentedControl
                            size="xs"
                            value={vm.view}
                            onChange={(value) => void vm.setView(value === 'team' ? 'team' : 'user')}
                            data={[
                                { label: 'You', value: 'user' },
                                { label: 'Team', value: 'team' },
                            ]}
                        />
                    </Group>
                    {vm.view === 'user' && cohortOptions.length > 0 && (
                        <Group gap={6} wrap="nowrap" align="center">
                            <Tooltip label="Benchmark your scores against this peer group — the radar overlay and your standing update to match" withArrow multiline w={240} position="bottom">
                                <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: 10, letterSpacing: '0.04em', cursor: 'help' }}>
                                    Compare to
                                </Text>
                            </Tooltip>
                            <SegmentedControl
                                size="xs"
                                value={effectiveCohortKind}
                                onChange={(value) => setCohortKind(value as CohortKind)}
                                data={cohortOptions}
                            />
                        </Group>
                    )}
                    <InsightsRangeControl
                        presets={vm.ranges.presets}
                        saved={vm.ranges.saved}
                        value={vm.rangeKey}
                        saving={vm.savingRange}
                        onSelect={(key) => void vm.setRange(key)}
                        onSaveRange={(label, start, end) => vm.saveCustomRange(label, start, end)}
                        onDeleteRange={(id) => void vm.deleteRange(id)}
                    />
                </Group>
            }
        />
        </Box>
    );

    if (snapshot === null) {
        return (
            <Box pb="xl">
                {header}
                <Text c={vm.error ? 'red' : 'dimmed'} mt="xl">
                    {vm.error ?? 'Loading your scorecard…'}
                </Text>
            </Box>
        );
    }

    if (vm.view === 'team') {
        return (
            <Box pb="xl">
                {header}
                {snapshot.team ? (
                    <InsightsTeamSection team={snapshot.team} />
                ) : (
                    <Text c="dimmed" mt="xl">
                        No team data available for this period.
                    </Text>
                )}
            </Box>
        );
    }

    const days = periodDays(snapshot.period.startIso, snapshot.period.endIso);
    return (
        <Box pb="xl">
            {header}
            <Stack gap="md">
                <InsightsSection
                    title="Scorecard"
                    accent="indigo"
                    caption="Your composite score, and where you stand among your peers."
                    summary={`Fluency ${snapshot.scorecard.fluencyScore}/100 · ${snapshot.scorecard.fluencyDelta >= 0 ? '+' : ''}${snapshot.scorecard.fluencyDelta} vs prior`}
                >
                    <InsightsScorecardSection scorecard={snapshot.scorecard} standing={snapshot.standing} cohortKind={effectiveCohortKind} showDelta={true} />
                </InsightsSection>
                {snapshot.adoption && (
                    <InsightsSection
                        title="Adoption"
                        accent="teal"
                        caption="How consistently and deeply you've adopted AI — a simple average of your four adoption drivers."
                        summary={`Adoption ${snapshot.adoption.score}/100 · Frequency ${snapshot.adoption.frequency} · Consistency ${snapshot.adoption.consistency}`}
                    >
                        <InsightsAdoptionSection adoption={snapshot.adoption} />
                    </InsightsSection>
                )}
                <InsightsSection
                    title="Activity & Engagement"
                    accent="blue"
                    caption="Your working rhythm with AI — daily contributions, session cadence, and model mix."
                    summary={`${snapshot.activity.sessions} sessions · ${snapshot.activity.activeDays} active days · ${formatCompact(snapshot.activity.totalTokens)} tokens`}
                >
                    <InsightsActivitySection activity={snapshot.activity} periodDays={days} periodStartIso={snapshot.period.startIso} periodEndIso={snapshot.period.endIso} />
                </InsightsSection>
                <InsightsSection
                    title="Highlights"
                    accent="amber"
                    caption="Where you demonstrated strong AI efficiency this period."
                    summary={`${snapshot.wins.oneShotRate}% one-shot success · ${snapshot.wins.complianceStreakDays} active days`}
                >
                    <InsightsWinsSection wins={snapshot.wins} />
                </InsightsSection>
                <InsightsSection
                    title="Challenges"
                    accent="rose"
                    caption="The refinement loops and long sessions quietly costing you time — visible only to you."
                    summary={`${snapshot.friction.sessions.filter((s) => s.stuck).length} long sessions to revisit`}
                >
                    <InsightsFrictionSection friction={snapshot.friction} />
                </InsightsSection>
                <InsightsSection
                    title="Usage Efficiency"
                    accent="violet"
                    caption="How efficiently you use tokens — topic focus, model fit, and wasted overhead, ranked against your peers."
                    summary={
                        snapshot.efficiency.totalTokens === 0
                            ? 'No token-metered usage this period'
                            : `${formatCompact(snapshot.efficiency.estimatedWastedTokens)} tokens of overhead · ${snapshot.efficiency.efficiencyTier} tier`
                    }
                >
                    <InsightsEfficiencySection
                        efficiency={snapshot.efficiency}
                        cohort={selectedCohort ?? null}
                        periodStartIso={snapshot.period.startIso}
                        periodEndIso={snapshot.period.endIso}
                    />
                </InsightsSection>
                <InsightsSection
                    title="Cost Saving Opportunities"
                    accent="green"
                    caption="What your usage costs — from real allocated spend — and where tighter habits would save."
                    summary={
                        snapshot.efficiency.totalCost === 0
                            ? 'No usage cost this period'
                            : ((): string => {
                                  const c = computeCostModel(snapshot.efficiency);
                                  return `${usd(c.totalCost)} total · ${usd(c.estimatedWaste)} estimated waste`;
                              })()
                    }
                >
                    <InsightsCostSection efficiency={snapshot.efficiency} />
                </InsightsSection>
                <InsightsSection
                    title="Use Case Expansion"
                    accent="cyan"
                    caption={`Additional ways to use AI in your role${snapshot.useCases.jobTitle ? ` as ${snapshot.useCases.jobTitle}` : ''}.`}
                    summary={`${snapshot.useCases.cards.length} additional ways to use AI`}
                >
                    <InsightsUseCasesSection useCases={snapshot.useCases} />
                </InsightsSection>
                <InsightsSection
                    title="Personal Development Plan"
                    accent="pink"
                    caption="Specific goals to level up your scorecard next period — one per category."
                    summary={`${snapshot.plan.assignments.length} development goals`}
                >
                    <InsightsPlanSection plan={snapshot.plan} />
                </InsightsSection>
            </Stack>
        </Box>
    );
}
